'use client';

import { FC, ReactNode, useRef } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';

type TooltipProps = {
  className?: string;
  overlay?: boolean;
  arrow?: boolean;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  hover?: boolean;
  duration?: number;
  trigger: ReactNode;
  children: ReactNode;
};

const contentClassName = {
  top: {
    start: '-top-10 left-0 transform -translate-y-full',
    center: '-top-10 left-1/2 transform -translate-y-full -translate-x-1/2',
    end: '-top-10 right-0 transform -translate-y-full',
  },
  bottom: {
    start: '-bottom-10 left-0 transform translate-y-full',
    center: '-bottom-10 left-1/2 transform translate-y-full -translate-x-1/2',
    end: '-bottom-10 right-0 transform translate-y-full',
  },
  left: {
    start: 'left-0 top-0 transform -translate-x-full',
    center: 'left-0 top-1/2 transform -translate-x-full -translate-y-1/2',
    end: 'left-0 bottom-0 transform -translate-x-full',
  },
  right: {
    start: 'right-0 top-0 transform translate-x-full',
    center: 'right-0 top-1/2 transform translate-x-full -translate-y-1/2',
    end: 'right-0 bottom-0 transform translate-x-full',
  },
};

const arrowClassName = {
  top: {
    start:
      'bottom-[-8px] left-[2px] border-x-8 border-t-8 border-t-neutral-800 border-x-transparent',
    center:
      'bottom-[-8px] left-1/2 transform -translate-x-1/2 border-x-8 border-t-8 border-t-neutral-800 border-x-transparent',
    end: 'bottom-[-8px] right-[2px] border-x-8 border-t-8 border-t-neutral-800 border-x-transparent',
  },
  bottom: {
    start:
      'top-[-4px] left-4 border-x-8 border-b-8 border-b-neutral-200 border-x-transparent',
    center:
      'top-[-4px] left-1/2 transform -translate-x-1/2 border-x-8 border-b-8 border-b-neutral-200 border-x-transparent',
    end: 'top-[-4px] right-4 border-x-8 border-b-8 border-b-neutral-200 border-x-transparent',
  },
  left: {
    start:
      'right-[-4px] top-4 border-y-8 border-l-8 border-l-neutral-200 border-y-transparent',
    center:
      'right-[-4px] top-1/2 transform -translate-y-1/2 border-y-8 border-l-8 border-l-neutral-200 border-y-transparent',
    end: 'right-[-4px] bottom-4 border-y-8 border-l-8 border-l-neutral-200 border-y-transparent',
  },
  right: {
    start:
      'left-[-4px] top-4 border-y-8 border-r-8 border-r-neutral-200 border-y-transparent',
    center:
      'left-[-4px] top-1/2 transform -translate-y-1/2 border-y-8 border-r-8 border-r-neutral-200 border-y-transparent',
    end: 'left-[-4px] bottom-4 border-y-8 border-r-8 border-r-neutral-200 border-y-transparent',
  },
};

const Tooltip: FC<TooltipProps> = ({
  className,
  overlay,
  arrow,
  side = 'top',
  align = 'start',
  hover,
  duration,
  trigger,
  children,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <div
          className="cursor-pointer"
          onMouseEnter={() => {
            if (!hover) return;
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current as unknown as number);
            }
            !open && triggerRef.current?.click();
          }}
          onMouseLeave={() => {
            if (!hover) return;
            timeoutRef.current = setTimeout(() => {
              if (open) close();
            }, 50) as unknown as NodeJS.Timeout;
          }}
        >
          <Popover.Button
            ref={triggerRef}
            className="focus:outline-none"
            onClick={() => duration && setTimeout(close, duration)}
          >
            {trigger}
          </Popover.Button>

          {overlay && (
            <Popover.Overlay className="fixed inset-0 bg-neutral-800 opacity-30" />
          )}

          <Transition
            className="absolute z-50"
            show={open}
            enter="transition duration-100 ease-out"
            enterFrom="scale-95 opacity-0"
            enterTo="scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="scale-100 opacity-100"
            leaveTo="scale-95 opacity-0"
          >
            {(ref) => (
              <Popover.Panel
                ref={ref}
                className={twMerge(
                  'absolute p-4 bg-neutral-800 rounded-md drop-shadow-xl whitespace-normal',
                  contentClassName[side][align],
                  className,
                )}
              >
                {children}
                {arrow && (
                  <div
                    className={twMerge('absolute w-0 h-0', arrowClassName[side][align])}
                  />
                )}
              </Popover.Panel>
            )}
          </Transition>
        </div>
      )}
    </Popover>
  );
};

export default Tooltip;
